import "./App.css";

import { useState, useEffect, memo, useRef } from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material/styles";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import CommentIcon from "@mui/icons-material/Comment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import Popper from "@mui/material/Popper";
import { StyledTooltip, getRichTooltipSx } from "./themeConfig";
import Stack from "@mui/material/Stack";
import RepeatIcon from "@mui/icons-material/Repeat";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import PaletteIcon from "@mui/icons-material/Palette";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import Slider from "@mui/material/Slider";
import { MessageItem } from "./MessageItem";
import { StoryItem } from "./StoryItem";
import * as lodash from "lodash";
import { sortCondition } from "./SendMessageBox";

import { useExitToApp, useHttpPost, useSetMessageProps } from "./hooks";

import {
  debugToggleState,
  projectPickerState,
  searchState,
  AccountInfo,
} from "./JotaiAtoms";
import {
  FONT_SIZE,
  ICON_FONT_SIZE,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  DEFAULT_FONT_SIZE,
} from "./constants";

import { UserName, QuoteBar } from "./Markdown";

import { isGithub, isGitlab, getUser, getTimestamp } from "./storydat";

import PouchDB from "pouchdb-browser";
import {
  Wheel,
  hsvaToHex,
  hexToHsva,
  hexToRgba,
  rgbaToHex,
  getContrastingColor,
  HsvaColor,
  RgbaColor,
} from "@uiw/react-color";
import Swatch from "@uiw/react-color-swatch";

import MessagePreview from "./MessagePreview";
import * as u from "./utility";
import { useAtomValue, useSetAtom } from "jotai";

// Module-level hover coordination (not a global listener)
let pendingHoverCheck: {
  mouseX: number;
  mouseY: number;
  timestamp: number;
} | null = null;
let sharedMousePos: { x: number; y: number } | null = null;
let activeRows: Map<
  string,
  {
    ref: React.RefObject<HTMLTableRowElement>;
    setHover: (hover: boolean) => void;
    lastProcessedTimestamp: number;
  }
> = new Map();

// Single interval for all rows (runs only when there's a pending check)
let globalIntervalId: NodeJS.Timeout | null = null;

function startGlobalHoverCheck() {
  if (globalIntervalId) return; // Already running

  globalIntervalId = setInterval(() => {
    if (!pendingHoverCheck) {
      // Nothing to check, stop the interval
      if (globalIntervalId) {
        clearInterval(globalIntervalId);
        globalIntervalId = null;
      }
      return;
    }

    const timeSinceCheck = Date.now() - pendingHoverCheck.timestamp;
    if (timeSinceCheck > 150) {
      // Check expired, clear it and stop interval
      pendingHoverCheck = null;
      if (globalIntervalId) {
        clearInterval(globalIntervalId);
        globalIntervalId = null;
      }
      return;
    }

    // Check all active rows
    activeRows.forEach((rowData, itemSK) => {
      if (rowData.lastProcessedTimestamp === pendingHoverCheck!.timestamp) {
        return; // Already processed this check
      }

      if (!rowData.ref.current) return;

      const rect = rowData.ref.current.getBoundingClientRect();
      const isUnderCursor =
        pendingHoverCheck!.mouseX >= rect.left &&
        pendingHoverCheck!.mouseX <= rect.right &&
        pendingHoverCheck!.mouseY >= rect.top &&
        pendingHoverCheck!.mouseY <= rect.bottom;

      rowData.lastProcessedTimestamp = pendingHoverCheck!.timestamp;

      if (isUnderCursor) {
        rowData.setHover(true);
      }
    });
  }, 20);
}

/**
 * Determines the position of a row within a group of adjacent rows with bgcolor.
 * Used to apply rounded corners appropriately.
 */
type BgColorPosition = "single" | "first" | "middle" | "last" | "none";

function getBgColorPosition(
  messages: MessageItem[],
  index: number
): BgColorPosition {
  const hasBgColor = messages[index]?.st_ui?.bgcolor;

  if (!hasBgColor) {
    return "none";
  }

  const prevHasBgColor = index > 0 && messages[index - 1]?.st_ui?.bgcolor;
  const nextHasBgColor =
    index < messages.length - 1 && messages[index + 1]?.st_ui?.bgcolor;

  if (!prevHasBgColor && !nextHasBgColor) {
    return "single";
  } else if (!prevHasBgColor && nextHasBgColor) {
    return "first";
  } else if (prevHasBgColor && !nextHasBgColor) {
    return "last";
  } else {
    return "middle";
  }
}

function getBorderRadiusForBgPosition(position: BgColorPosition): string {
  switch (position) {
    case "single":
      return "12px";
    case "first":
      return "12px 12px 0 0";
    case "last":
      return "0 0 12px 12px";
    case "middle":
    case "none":
    default:
      return "0";
  }
}

/**
 * Generates sx object for styling links within MessagePreview.
 * Used to apply custom link colors based on row background.
 */
function getLinkSx(linkColor?: string): any {
  if (!linkColor) {
    return undefined;
  }
  return {
    "&.MuiLink-root": {
      color: linkColor,
    },
  };
}

function Point({ color, checked }: { color?: string; checked?: boolean }) {
  if (!checked || !color) return null;
  return (
    <div
      style={{
        height: 5,
        width: 5,
        borderRadius: "50%",
        backgroundColor: getContrastingColor(color),
      }}
    />
  );
}

function getIconColor(backgroundColor: string) {
  const b = hexToRgba(backgroundColor);
  const delta = 128;
  const c: RgbaColor = {
    r: (delta + b.r) % 255,
    b: (delta + b.b) % 255,
    g: (delta + b.g) % 255,
    a: 1,
  };
  return rgbaToHex(c);
}

type ColorPickerProps = {
  color: string;
  onChange: (color: string | undefined) => void;
  fontSize: number;
  onChangeFontSize: (size: number) => void;
  iconColor: string;
};

function ColorPicker({
  color,
  onChange,
  fontSize,
  onChangeFontSize,
  iconColor,
}: ColorPickerProps) {
  const theme = useTheme();
  const [hsva, setHsva] = useState<HsvaColor>(hexToHsva(color));
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [sliderPos, setSliderPos] = useState<number>(fontSize);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? undefined : e.currentTarget);
  };

  const handleColorChange = (color: { hsva: HsvaColor }) => {
    const res = { ...hsva, ...color.hsva };
    setHsva(res);

    const hex = hsvaToHex(res);
    onChange(hex);
  };

  const handleResetColor = () => {
    // Reset to white in the color picker UI
    const white = hexToHsva("#ffffff");
    setHsva(white);
    // But tell parent to clear the background (use transparent)
    onChange(undefined);
  };

  const handleSlider = (e: Event, v: number | number[]) => {
    if (typeof v === "number") {
      setSliderPos(v);
      onChangeFontSize(v);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        id={anchorEl ? "color-picker-popper" : undefined}
        sx={{ p: 0.75 }}
      >
        <PaletteIcon fontSize="small" sx={{ color: iconColor }} />
      </IconButton>
      <Popper
        id={anchorEl ? "color-picker-popper" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        sx={{ zIndex: 2000 }}
      >
        <Box
          sx={{
            ...getRichTooltipSx(theme.palette.mode === "dark"),
            p: 1.5,
            m: 1,
            mr: 3,
            ml: 3,
            position: "relative",
            display: "inline-block",
          }}
        >
          <Box sx={{ position: "absolute", right: 0.4, top: 2, zIndex: 5 }}>
            <IconButton size="small" onClick={handleResetColor}>
              <FormatColorResetIcon
                sx={{
                  color: theme.palette.mode === "dark" ? "#d0d0d0" : "#505050",
                  fontSize: ICON_FONT_SIZE,
                }}
              />
            </IconButton>
          </Box>
          <Stack direction="row" gap={1} alignItems={"center"} sx={{ pr: 1.5 }}>
            <Box sx={{ height: 100 }}>
              <Slider
                aria-label="Font Size"
                orientation="vertical"
                value={sliderPos}
                onChange={handleSlider}
                sx={{
                  color: theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                  "& .MuiSlider-thumb": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                  },
                  "& .MuiSlider-track": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                  },
                }}
                min={MIN_FONT_SIZE}
                max={MAX_FONT_SIZE}
                step={1}
                valueLabelDisplay="auto"
              />
            </Box>
            <Stack direction="column" alignItems={"center"} gap={0}>
              <Swatch
                colors={["#f4b5de", "#fed2bf", "#fcf9a6", "#badfb9", "#bbc0df"]}
                color={hsvaToHex(hsva)}
                rectProps={{
                  children: <Point />,
                  style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
                onChange={(hsva) => handleColorChange({ hsva: hsva })}
              />
              <Wheel
                color={hsva}
                onChange={handleColorChange}
                width={100}
                height={100}
              />
            </Stack>
          </Stack>
        </Box>
      </Popper>
    </>
  );
}

type ThreadRowProps = {
  item: MessageItem;
  sx: any;
  nodebug: boolean;
  expand?: boolean;
  overflow?: boolean;
  storyKey?: string;
  disjoin: Set<string>;
  setMessages?: (value: React.SetStateAction<MessageItem[]>) => void;
  story?: StoryItem;
  allowReplay: boolean;
  bgPosition?: BgColorPosition;
};

type Controls = {
  rawSource?: boolean;
  copy?: boolean;
  restore?: boolean;
  replay?: boolean;
  copyLink?: boolean;
  disjoin?: boolean;
  disjoinThread?: boolean;
  colorize?: boolean;
  delete?: boolean;
};

export function ThreadRow({
  item,
  sx,
  nodebug,
  expand,
  overflow,
  storyKey,
  disjoin,
  setMessages,
  story,
  allowReplay,
  bgPosition = "none",
}: ThreadRowProps) {
  const theme = useTheme();
  const [rawJson, setRawJson] = useState<boolean>(false);
  const [fontSize, setFontSize] = useState<number>(
    (item.st_ui && item.st_ui.font_size) || FONT_SIZE[0]
  );
  const [hover, setHover] = useState<boolean>(false);
  const [color, setColor] = useState<string | undefined>(item.st_ui?.bgcolor);
  const httpPost = useHttpPost();
  const setMessageProps = useSetMessageProps();
  const projectName = useAtomValue(projectPickerState);
  const [controls, setControls] = useState<Controls>({});
  const rowRef = useRef<HTMLTableRowElement>(null);

  // useEffect(() => {
  //   const key = item.old_dummy_message
  //     ? (item.old_dummy_message.PK || "dummy") + "#" + item.old_dummy_message.SK
  //     : (item.PK || "dummy") + "#" + item.SK;

  //   console.log(`mount ${key}`);
  // }, []);

  // Register this row with the global hover check system
  useEffect(() => {
    activeRows.set(item.SK, {
      ref: rowRef,
      setHover: setHover,
      lastProcessedTimestamp: 0,
    });

    return () => {
      activeRows.delete(item.SK);
    };
  }, [item.SK]);

  useEffect(() => {
    let newControls: Controls = {};
    if (!nodebug && !expand) {
      newControls.rawSource = true;
    }
    if (!nodebug) {
      newControls.copy = true;
    }

    if (!nodebug && !allowReplay && item.st_hide) {
      newControls.restore = true;
    }

    if (allowReplay) {
      newControls.replay = true;
      newControls.copyLink = true;
    }

    if (!allowReplay && item.st_auto_joined) {
      newControls.disjoin = true;
    }

    if (!item.dummy && !allowReplay && disjoin.has(`${item.PK}#${item.SK}`)) {
      newControls.disjoinThread = true;
    }

    if (!item.dummy && !allowReplay) {
      newControls.colorize = true;
      newControls.delete = true;
    }

    if (!lodash.isEqual(newControls, controls)) {
      setControls(newControls);
    }
  }, [nodebug, expand, item, allowReplay, disjoin]);

  const handleReplay = (item: MessageItem) => {
    const replay = async () => {
      try {
        httpPost("label", { replaylog: { events: [item] } });
      } catch (err) {
      } finally {
      }
    };

    replay();
  };

  const handleDisjoinMessage = (item: MessageItem) => {
    const request = async () => {
      try {
        const data = { disjoin: { thread: item.PK, message: item.SK } };
        httpPost("label", data);
      } catch (err) {}
    };

    if (!setMessages) {
      return;
    }

    setMessages((prev: MessageItem[]) => {
      let index = prev.findIndex((e: MessageItem) => e.SK === item.SK);

      if (index !== -1) {
        request();
        const result = Array.from(prev);
        result.splice(index, 1);
        return result;
      }

      return prev;
    });
  };

  const handleDisjoinThread = (item: MessageItem) => {
    if (!story) {
      return;
    }

    const request = async (threadId: string) => {
      try {
        httpPost("label", { disjoin: { story: story.SK, thread: threadId } });
      } catch (err) {
      } finally {
      }
    };

    const threadId = item.PK.split("#").slice(1).join("#");
    request(threadId);

    if (!setMessages) {
      return;
    }

    setMessages((prev: MessageItem[]) => {
      return prev.filter((e: MessageItem) => e.PK !== item.PK);
    });
  };

  const removeThreadFromCache = () => {
    if (story) {
      if (projectName) {
        const db = new PouchDB(projectName);
        const key = `Thread:${story.PK}:${story.SK}`;
        db.get(key)
          .then((doc) => {
            db.remove(doc).catch((e) => {});
          })
          .catch((err) => {});
      }
    }
  };

  const handleCloseMessage = (item: MessageItem) => {
    if (setMessages) {
      setMessages((prev: MessageItem[]) => {
        let newMessages = [...prev];
        let found = newMessages.find(
          (m) => m.SK === item.SK && m.PK === item.PK
        );
        if (found) {
          found.st_hide = true;
        }
        return newMessages;
      });
    }

    removeThreadFromCache();

    setMessageProps(item, "st_hide", true);

    // After deletion, set module-level hover check variable
    setTimeout(() => {
      if (sharedMousePos) {
        pendingHoverCheck = {
          mouseX: sharedMousePos.x,
          mouseY: sharedMousePos.y,
          timestamp: Date.now(),
        };
        startGlobalHoverCheck();
      }
    }, 50);
  };

  const handleRestoreMessage = (item: MessageItem) => {
    if (setMessages) {
      setMessages((prev: MessageItem[]) => {
        let newMessages = [...prev];
        let found = newMessages.find(
          (m) => m.SK === item.SK && m.PK === item.PK
        );
        if (found) {
          found.st_hide = false;
        }
        return newMessages;
      });
    }

    removeThreadFromCache();
    setMessageProps(item, "st_hide", false);
  };

  const toggleRawJson = () => {
    setRawJson((prev) => !prev);
  };

  const getMessageJson = () => {
    const messageJsonStr = JSON.stringify(item, null, 2);
    return messageJsonStr;
  };

  const commitAppearence = (key: string, value?: string | number | null) => {
    setMessageProps(item, `st_ui/${key}`, value);

    if (setMessages) {
      setMessages((prev: MessageItem[]) => {
        let index = prev.findIndex((e: MessageItem) => e.SK === item.SK);

        if (index !== -1) {
          const next = [...prev];
          if (!next[index].st_ui) {
            next[index].st_ui = {};
          }
          if (value !== undefined) {
            next[index].st_ui[key] = value;
          } else {
            delete next[index].st_ui[key];
          }

          return next;
        }

        return prev;
      });
    }

    removeThreadFromCache();
  };

  const handleFontChange = (newSize: number) => {
    setFontSize(newSize);

    commitAppearence(
      "font_size",
      newSize === DEFAULT_FONT_SIZE ? null : newSize
    );
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    sharedMousePos = { x: e.clientX, y: e.clientY };
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    sharedMousePos = { x: e.clientX, y: e.clientY };
  };

  const handleColorChange = (newColor: string | undefined) => {
    setColor(newColor);
    // Save the color when user customizes it, or clear it if undefined
    commitAppearence("bgcolor", newColor);
  };

  const pickBgColor = () => {
    if (!color) return "transparent";
    if (!hover) return color;
    const mixColor = theme.palette.mode === "dark" ? "#404040" : "#111111";
    return u.mixColors(color, mixColor, 0.95);
  };
  const pickIconColor = () => {
    const bgColor = pickBgColor();
    return bgColor === "transparent"
      ? theme.palette.text.primary
      : getIconColor(bgColor);
  };
  const pickTextColor = () => {
    const bgColor = pickBgColor();
    return bgColor === "transparent" ? "inherit" : getContrastingColor(bgColor);
  };
  const pickLinkColor = () => {
    const bgColor = pickBgColor();
    if (bgColor === "transparent") {
      return undefined; // Use default link color
    }
    // Calculate luminance to determine if background is light or dark
    const rgba = hexToRgba(bgColor);
    const luminance = (0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b) / 255;
    // For dark backgrounds (low luminance), use light blue
    // For light backgrounds (high luminance), use dark blue
    return luminance > 0.5 ? "#1967d2" : "#8ab4f8";
  };

  const getCodeLineStyles = () => {
    const bgColor = pickBgColor();
    if (bgColor === "transparent") {
      return {}; // Use default CSS variables
    }
    // Calculate luminance to determine if background is light or dark
    const rgba = hexToRgba(bgColor);
    const luminance = (0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b) / 255;

    // CodeLine: subtle adjustment
    const codeLineBg =
      luminance > 0.5
        ? u.mixColors(bgColor, "#000000", 0.92) // Darken by 8%
        : u.mixColors(bgColor, "#ffffff", 0.9); // Lighten by 10%
    const codeLineText = luminance > 0.5 ? "#4b5563" : "#d1d5db";

    // CodeBlock: heavier presence with more pronounced adjustment
    const codeBlockBg =
      luminance > 0.5
        ? u.mixColors(bgColor, "#000000", 0.82) // Darken by 18%
        : u.mixColors(bgColor, "#ffffff", 0.8); // Lighten by 20%
    const codeBlockText = luminance > 0.5 ? "#374151" : "#e5e7eb";

    // QuoteBar: subtle background tint with colored border
    const quoteBg =
      luminance > 0.5
        ? u.mixColors(bgColor, "#000000", 0.95) // Very subtle darken by 5%
        : u.mixColors(bgColor, "#ffffff", 0.95); // Very subtle lighten by 5%
    const quoteBorder =
      luminance > 0.5
        ? u.mixColors(bgColor, "#000000", 0.7) // Darken by 30%
        : u.mixColors(bgColor, "#ffffff", 0.7); // Lighten by 30%

    return {
      "--md-code-bg": codeLineBg,
      "--md-code-text": codeLineText,
      "--code-bg": codeBlockBg,
      "--code-text": codeBlockText,
      "--quote-bg": quoteBg,
      "--quote-border": quoteBorder,
    };
  };

  const getSelectionStyles = () => {
    const bgColor = pickBgColor();
    if (bgColor === "transparent") {
      return {}; // Use browser default
    }
    // Calculate luminance to determine if background is light or dark
    const rgba = hexToRgba(bgColor);
    const luminance = (0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b) / 255;
    // For custom backgrounds, provide high-contrast selection
    const selectionBg =
      luminance > 0.5
        ? "rgba(0, 100, 200, 0.3)" // Dark blue overlay for light backgrounds
        : "rgba(100, 180, 255, 0.4)"; // Light blue overlay for dark backgrounds
    const selectionColor = luminance > 0.5 ? "#000000" : "#ffffff";
    return {
      "& ::selection": {
        backgroundColor: selectionBg,
        color: selectionColor,
      },
      "& ::-moz-selection": {
        backgroundColor: selectionBg,
        color: selectionColor,
      },
    };
  };

  const borderRadius = getBorderRadiusForBgPosition(bgPosition);

  return (
    <TableRow
      ref={rowRef}
      hover={false}
      sx={{
        ...sx,
        "& td, & th": {
          borderBottom: "none",
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <TableCell
        component="th"
        scope="row"
        align="left"
        size="small"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "normal",
          pb: 1.5,
          pt: 1.5,
          pr: 2,
          minHeight: "18px",
          color: pickTextColor(),
          backgroundColor: pickBgColor(),
          borderRadius: borderRadius,
          ...getCodeLineStyles(),
          ...getSelectionStyles(),
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"flex-start"}
          justifyContent={"space-between"}
        >
          <Stack direction={"column"} sx={{ width: "100%" }}>
            <MemoMessage
              item={item}
              storyKey={nodebug ? storyKey : ""}
              overflow={overflow}
              fontSize={fontSize}
              textColor={pickTextColor()}
              linkColor={pickLinkColor()}
              iconColor={pickIconColor()}
            />

            {(expand || rawJson) && (
              <Typography
                sx={{ color: "text.secondary", fontSize: 10 }}
                component={"span"}
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {getMessageJson()}
                </pre>
              </Typography>
            )}
          </Stack>
          <Box
            sx={{
              minWidth: 32 * Object.keys(controls).length,
              height: 20,
              ml: 2,
            }}
          >
            {hover && (
              <Stack direction={"row"} justifyContent="flex-end" gap={0.5}>
                {controls.rawSource && (
                  <IconButton
                    size="small"
                    onClick={() => toggleRawJson()}
                    sx={{ p: 0.75 }}
                  >
                    <CommentIcon
                      fontSize="small"
                      sx={{ color: pickIconColor() }}
                    />
                  </IconButton>
                )}
                {controls.copy && (
                  <StyledTooltip title="Copy JSON to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(getMessageJson());
                      }}
                      sx={{ p: 0.75 }}
                    >
                      <ContentCopyIcon
                        fontSize="small"
                        sx={{ color: pickIconColor() }}
                      />
                    </IconButton>
                  </StyledTooltip>
                )}

                {controls.restore && (
                  <StyledTooltip title="Restore message">
                    <IconButton
                      size="small"
                      onClick={() => handleRestoreMessage(item)}
                      sx={{ p: 0.75 }}
                    >
                      <RestoreFromTrashIcon
                        fontSize="small"
                        sx={{ color: pickIconColor() }}
                      />
                    </IconButton>
                  </StyledTooltip>
                )}

                {controls.replay && (
                  <StyledTooltip title="Replay event">
                    <IconButton
                      size="small"
                      onClick={() => handleReplay(item)}
                      sx={{ p: 0.75 }}
                    >
                      <RepeatIcon
                        fontSize="small"
                        sx={{ color: pickIconColor() }}
                      />
                    </IconButton>
                  </StyledTooltip>
                )}

                {controls.copyLink && (
                  <StyledTooltip title="Copy link to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          window.location.protocol +
                            "//" +
                            window.location.host +
                            "/message/" +
                            projectName +
                            "/" +
                            item.SK +
                            "/" +
                            window.location.search
                        );
                      }}
                      sx={{ p: 0.75 }}
                    >
                      <LinkIcon
                        fontSize="small"
                        sx={{ color: pickIconColor() }}
                      />
                    </IconButton>
                  </StyledTooltip>
                )}

                {controls.disjoin && (
                  <StyledTooltip title="Disjoin automatically linked message">
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleDisjoinMessage(item);
                      }}
                      sx={{ p: 0.75 }}
                    >
                      <CallSplitIcon
                        fontSize="small"
                        sx={{ color: pickIconColor() }}
                      />
                    </IconButton>
                  </StyledTooltip>
                )}

                {controls.disjoinThread && (
                  <StyledTooltip title="Disjoin thread">
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleDisjoinThread(item);
                      }}
                      sx={{ p: 0.75 }}
                    >
                      <CallSplitIcon
                        fontSize="small"
                        sx={{ color: pickIconColor() }}
                      />
                    </IconButton>
                  </StyledTooltip>
                )}

                {controls.colorize && (
                  <ColorPicker
                    color={color || "#ffffff"}
                    onChange={handleColorChange}
                    fontSize={fontSize}
                    onChangeFontSize={handleFontChange}
                    iconColor={pickIconColor()}
                  />
                )}

                {controls.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleCloseMessage(item)}
                    sx={{ p: 0.75 }}
                  >
                    <DeleteOutlineIcon
                      fontSize="small"
                      sx={{ color: pickIconColor() }}
                    />
                  </IconButton>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

type ThreadProps = {
  messages: MessageItem[];
  setMessages?: (value: React.SetStateAction<MessageItem[]>) => void;
  allowReplay: boolean;
  expand?: boolean;
  story?: StoryItem;
  background?: string;
  overflow?: boolean;
};

export function Thread({
  messages,
  setMessages,
  allowReplay,
  expand,
  story,
  background,
  overflow,
}: ThreadProps) {
  const theme = useTheme();
  const [disjoin, setDisjoin] = useState<Set<string>>(new Set());
  const nodebug = useAtomValue(debugToggleState);
  const storyKey: string =
    story && "SK4" in story ? `${Number(story.SK4)}` : "";

  const getRowStyle = (item: MessageItem) => {
    if (item.st_ui && item.st_ui.notif) {
      for (let notif_date of item.st_ui.notif) {
        const date = u.parsePythonTimestamp(notif_date);
        if (u.isTodayDate(date)) {
          return {
            background:
              theme.palette.mode === "dark"
                ? "rgba(242, 139, 130, 0.15)"
                : "#f5eeee",
          };
        }
      }
    }

    return {};
  };

  useEffect(() => {
    let disjoin = new Set<string>();
    let visited = new Set<string>();
    if (messages && messages.length > 0) {
      const first = messages[0];
      const last = messages[messages.length - 1];
      const fwd = first.SK < last.SK;

      let starter = first.SK < last.SK ? first.PK : last.PK;
      if (story) {
        starter = story.PK.split("#")[0] + "#" + story.SK;
      }

      for (let i = 0; i < messages.length; ++i) {
        const index = fwd ? i : messages.length - 1 - i;

        const pk = messages[index].PK;
        const sk = messages[index].SK;

        if (pk !== starter && (!visited.has(pk) || messages[index].st_edit)) {
          disjoin.add(`${pk}#${sk}`);
          visited.add(pk);
        }
      }
    }
    setDisjoin(disjoin);
  }, [messages]);

  let filteredMessages: MessageItem[] = [];
  if (messages && messages.length) {
    if (!allowReplay && nodebug) {
      let edited = new Map<string, string[]>();

      // group edits by the message they apply to
      for (let m of messages) {
        let edit_ts = m.st_edit;
        if (m.subtype === "message_changed") {
          edit_ts = m.message.ts;
        }

        if (edit_ts) {
          if (!edited.has(edit_ts)) {
            edited.set(edit_ts, [m.SK]);
          } else {
            edited.get(edit_ts)!.push(m.SK);
          }
        }
      }
      // remove all edits except the most recent one
      let filterOut = new Set();
      for (let [k, v] of edited.entries()) {
        filterOut.add(k);
        const mostRecentEdit = v[0] > v[v.length - 1] ? v[0] : v[v.length - 1];
        for (let m of v) {
          if (m !== mostRecentEdit) {
            filterOut.add(m);
          }
        }
      }

      // remove deleted message and edits to the title message
      const titleEdit = story?.st_edit;
      const titleTs = story?.ts;
      for (let m of messages) {
        // remove deleted messages
        if (m.st_hide) {
          filterOut.add(m.SK);
        }

        // remove edits to the title story, we'll keep on the list only the
        // title message that is newer than the story
        if (
          titleEdit &&
          m.st_edit == titleEdit &&
          m.ts &&
          titleTs &&
          m.ts <= titleTs
        ) {
          filterOut.add(m.SK);
        } else if (
          m.ts === titleTs &&
          m.st_type == story?.st_type &&
          m.st_url == story?.st_url
        ) {
          // st_edit could be missing in which chase this is our way of filtering out the title story message
          // see [1862]
          // however, i do not really like the way i do this filtering out, would be way better
          // to rely on unique object id, for example
          filterOut.add(m.SK);
        }
      }

      // remove title message unless it was edited (see above) in which case
      // we'll have the most recent edit present in the thread
      // this is mostly for older stuff, before introduction of st_unique_object_id
      if (story && story.st_created_at && !edited.has(story.st_created_at)) {
        const ts = story.st_created_at;
        filterOut.add(story.SK);
        filterOut.add(story.st_created_at);
        if (story.st_unique_object_id) {
          if (story.ts) {
            filterOut.add(`${story.ts}#${story.st_unique_object_id}`);
          }
          if (story.st_created_at) {
            filterOut.add(`${story.ts}#${story.st_created_at}`);
          }
        }

        if (edited.has(ts)) {
          for (let e of edited.get(ts)!) {
            filterOut.add(e);
          }
        }
      }

      // remove duplicates, duplicates message might come from different threads
      // like tow merge requests might contain same commits, in which case we'll have two stories
      // with unique pairs PK+SK, but exact the same SK keys
      let seen = new Set();
      let uniqueMessages = [];
      for (let m of messages) {
        if (!seen.has(m.SK)) {
          seen.add(m.SK);
          uniqueMessages.push(m);
        }
      }

      filteredMessages = uniqueMessages.filter((item) => {
        return !filterOut.has(item.SK);
      });
    } else {
      filteredMessages = messages;
    }

    // Merge adjacent messages from same user/type without attachments and within 2 minutes
    /*
    const mergedMessages: MessageItem[] = [];
    for (let i = 0; i < filteredMessages.length; i++) {
      const current = filteredMessages[i];

      // Get timestamp for current message
      const currentTs = current.st_edit || current.ts;
      if (!currentTs) {
        // Can't merge without timestamp
        mergedMessages.push(current);
        continue;
      }

      // Check if we can merge with previous message
      if (mergedMessages.length > 0) {
        const prev = mergedMessages[mergedMessages.length - 1];
        const prevTs = prev.st_edit || prev.ts;

        // Check merge conditions
        const sameUser =
          (current.st_user && prev.st_user && current.st_user === prev.st_user) ||
          (current.st_user && prev.st_user && current.st_user === prev.st_user) ||
          (!current.st_user && !prev.st_user && current.st_user === prev.st_user);
        const sameType = current.st_type === prev.st_type;
        const noFilesInCurrent = !current.st_files || current.st_files.length === 0;
        const noFilesInPrev = !prev.st_files || prev.st_files.length === 0;

        if (sameUser && sameType && noFilesInCurrent && noFilesInPrev && prevTs) {
          // Check time distance (< 2 minutes = 120 seconds)
          const timeDiff = Math.abs(parseFloat(currentTs) - parseFloat(prevTs));

          if (timeDiff < 120) {
            // Merge current into previous
            if (prev.st_pp && current.st_pp) {
              // Merge markdown content with a line break
              prev.st_pp = prev.st_pp + '\n\n' + current.st_pp;
            } else if (prev.text && current.text) {
              // Merge plain text with a line break
              prev.text = prev.text + '\n\n' + current.text;
            }
            // Skip adding current message (it's been merged)
            continue;
          }
        }
      }

      // Add current message
      mergedMessages.push(current);
    }

    filteredMessages = mergedMessages;
    */

    // if a message has a parent and we have the parent in the list, quote it
    const with_parents = filteredMessages.filter((m) => m.parent);
    if (with_parents.length > 0) {
      // collect unique parent ids
      const uniq_parents = new Set<string>();
      for (let m of with_parents) {
        if (!uniq_parents.has(m.parent!)) {
          uniq_parents.add(m.parent!);
        }
      }

      // collect parent messages if we have them
      const parent_messages = new Map<string, MessageItem>();
      for (let m of uniq_parents) {
        const parent = filteredMessages.find((p) => p.SK.endsWith(`#${m}`));
        if (parent) {
          parent_messages.set(m, parent);
        }
      }

      // quote the parents
      // for (let m of with_parents) {
      //   let parent = parent_messages.get(m.parent!);
      //   if (parent) {
      //     m.quote = parent;
      //   }
      // }
      const order = sortCondition(
        filteredMessages[0],
        filteredMessages[filteredMessages.length - 1],
        true
      );
      const orderedItem = (index: number) => {
        if (order === -1) {
          return filteredMessages[index];
        } else {
          return filteredMessages[filteredMessages.length - 1 - index];
        }
      };

      const getPrevParent = (m: MessageItem) => {
        // examples
        // m.parent  "github.616419470.comment.14804652"
        // m.SK "1761659541.000000#github.616419470.comment.14804737"
        return m.parent || m.SK.split("#").slice(-1)[0];
      };

      let prevParent: string | undefined = getPrevParent(orderedItem(0));
      for (let i = 1; i < filteredMessages.length; ++i) {
        let m = orderedItem(i);
        if (m.parent && m.parent !== prevParent) {
          let parent = parent_messages.get(m.parent);
          if (parent) {
            m.quote = parent;
          }
        }
        prevParent = getPrevParent(m);
      }
    }
  }

  return (
    <TableContainer
      sx={{
        borderRadius: "6px",
      }}
    >
      <Table size="small" aria-label="simple table">
        <TableBody>
          {filteredMessages &&
            filteredMessages.map((item, index) => {
              const bgPosition = getBgColorPosition(filteredMessages, index);

              return (
                <ThreadRow
                  key={
                    item.old_dummy_message
                      ? (item.old_dummy_message.PK || "dummy") +
                        "#" +
                        item.old_dummy_message.SK
                      : (item.PK || "dummy") + "#" + item.SK
                  }
                  item={item}
                  nodebug={nodebug}
                  overflow={overflow}
                  expand={expand}
                  storyKey={storyKey}
                  disjoin={disjoin}
                  setMessages={setMessages}
                  story={story}
                  allowReplay={allowReplay}
                  bgPosition={bgPosition}
                  sx={{
                    // "&:last-child td, &:last-child th": { border: 0 },
                    ...(filteredMessages.length > 1 ? getRowStyle(item) : {}),
                  }}
                />
              );
            })}
          <TableRow>
            <TableCell sx={{ height: "15px" }} />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type ExternalAppIconProps = {
  item: MessageItem;
  fontSize?: number;
  iconColor?: string;
};

const getExternalAppIcon = (item: MessageItem) => {
  if (item.st_type === "commit") {
    return "pajamas:commit";
    // return 'octicon:git-commit-24';
    // return 'octicon:git-commit-16';
  } else if (item.st_type === "pr") {
    if (item.state === "merged") {
      return "pajamas:merge";
    } else if (item.state === "closed") {
      return "pajamas:merge-request-close";
    } else {
      return "pajamas:merge-request";
    }
  } else if (item.st_type === "issue") {
    if (item.state?.startsWith("open")) {
      return "octicon:issue-opened-16";
    } else if (item.state?.startsWith("closed")) {
      if (
        item.state === "closed_duplicate" ||
        item.state === "closed_not_planned"
      ) {
        return "octicon:skip-16";
      }
      return "octicon:issue-closed-16";
    }
    return "pajamas:issues";
  } else if (item.st_type == "comment") {
    return "octicon:comment-16";
  } else if (item.st_type === "diff") {
    return "octicon:diff";
  } else if (item.st_type === "discussion") {
    if (item.state === "closed") {
      return "octicon:discussion-closed-16";
    }
    return "octicon:comment-discussion-16";
  }
  if (isGithub(item)) {
    return "uil:github";
  } else if (isGitlab(item)) {
    return "ph:gitlab-logo-fill";
  }
  return undefined;
};

export function ExternalAppIcon({
  item,
  fontSize,
  iconColor,
}: ExternalAppIconProps) {
  const exitToApp = useExitToApp();

  return (
    <>
      {getExternalAppIcon(item) && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            exitToApp(item);
          }}
          sx={{
            p: 0.5,
            color: "var(--text-tertiary)",
            opacity: 0.5,
            transition: "opacity 0.2s ease",
            "&:hover": {
              opacity: 0.85,
              backgroundColor: "transparent",
            },
          }}
        >
          <Icon
            icon={getExternalAppIcon(item)!}
            fontSize={(fontSize || DEFAULT_FONT_SIZE) * 0.95}
          />
        </IconButton>
      )}
    </>
  );
}

type MessageProps = {
  item: MessageItem;
  storyKey?: string;
  overflow?: boolean;
  fontSize?: number;
  kind?: "story" | "reply";
  noAuthor?: boolean;
  noIcon?: boolean;
  textColor?: string;
  linkColor?: string;
  iconColor?: string;
};

export function MessageNoQuote({
  item,
  storyKey,
  overflow,
  kind,
  noAuthor,
  noIcon,
  fontSize,
  textColor,
  linkColor,
  iconColor,
}: MessageProps) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={1}>
      {!noIcon && getExternalAppIcon(item) && (
        <ExternalAppIcon
          item={item}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
          iconColor={iconColor}
        />
      )}
      <Stack direction="column" width="100%" gap={1}>
        {!noAuthor && (
          <Authorship
            item={item}
            kind={kind}
            textColor={textColor}
            linkColor={linkColor}
          />
        )}
        <MessagePreview
          item={item}
          storyKey={storyKey}
          overflow={overflow}
          fontSize={fontSize}
          readOnly={undefined}
          sx={getLinkSx(linkColor)}
        />
      </Stack>
    </Stack>
  );
}

export function Message({
  item,
  storyKey,
  overflow,
  kind,
  fontSize = DEFAULT_FONT_SIZE,
  textColor,
  linkColor,
  iconColor,
}: MessageProps) {
  return (
    <>
      {!item.quote && (
        <MessageNoQuote
          item={item}
          storyKey={storyKey}
          overflow={overflow}
          kind={kind}
          fontSize={fontSize}
          textColor={textColor}
          linkColor={linkColor}
          iconColor={iconColor}
        />
      )}
      {item.quote && (
        <>
          {item.parent && (
            <Stack direction="row" alignItems="flex-start" gap={1}>
              {getExternalAppIcon(item) && (
                <ExternalAppIcon
                  item={item}
                  fontSize={fontSize || DEFAULT_FONT_SIZE}
                  iconColor={iconColor}
                />
              )}
              <Stack direction={"column"} gap={1}>
                <Authorship
                  item={item}
                  kind={kind}
                  textColor={textColor}
                  linkColor={linkColor}
                />
                <QuoteBar>
                  <MessageNoQuote
                    item={item.quote}
                    storyKey={storyKey}
                    overflow={overflow}
                    fontSize={fontSize}
                    noAuthor
                    noIcon
                    textColor={textColor}
                    linkColor={linkColor}
                    iconColor={iconColor}
                  />
                </QuoteBar>
                <MessageNoQuote
                  item={item}
                  storyKey={storyKey}
                  overflow={overflow}
                  kind={kind}
                  fontSize={fontSize}
                  noAuthor
                  noIcon
                  textColor={textColor}
                  linkColor={linkColor}
                  iconColor={iconColor}
                />
              </Stack>
            </Stack>
          )}
          {!item.parent && (
            <Stack direction={"column"} gap={1}>
              <MessageNoQuote
                item={item}
                storyKey={storyKey}
                overflow={overflow}
                kind={kind}
                fontSize={fontSize}
                textColor={textColor}
                linkColor={linkColor}
                iconColor={iconColor}
              />
              <QuoteBar>
                <MessageNoQuote
                  item={item.quote}
                  storyKey={storyKey}
                  overflow={overflow}
                  fontSize={fontSize}
                  noAuthor
                  textColor={textColor}
                  linkColor={linkColor}
                  iconColor={iconColor}
                />
              </QuoteBar>
            </Stack>
          )}
        </>
      )}
    </>
  );
}

function getDisplayTime(item: MessageItem, kind?: "story" | "reply") {
  if (kind === "story") {
    if (!item.st_edit && item.ts) {
      return u.timestampToString(item.ts);
    } else if (item.st_edit) {
      return u.timestampToString(item.st_edit.split("#")[0]);
    }
  }

  return getTimestamp(item);
}

function Authorship({
  item,
  kind,
  textColor,
  linkColor,
}: {
  item: MessageItem;
  kind?: "story" | "reply";
  textColor?: string;
  linkColor?: string;
}) {
  const theme = useTheme();
  const setSearch = useSetAtom(searchState);
  const handleClick = (accounts: AccountInfo[]) => {
    setSearch([accounts]);
  };

  return (
    <Typography
      sx={{
        color:
          textColor || (theme.palette.mode === "dark" ? "#9aa0a6" : "#a0a0a0"),
        fontSize: 6,
        lineHeight: 1,
      }}
    >
      <span>
        <UserName
          user={getUser(item)}
          onClick={handleClick}
          fontSize={7}
          textColor={linkColor}
        />
      </span>
      <span>{` - ${u.tryLocalizeIsoDate(getDisplayTime(item, kind))}`}</span>
    </Typography>
  );
}

export const MemoMessage = memo(Message);
